import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  ActionIcon,
  Anchor,
  Box,
  Button,
  Group,
  Menu,
  Stack,
  Text,
  UnstyledButton,
} from "@mantine/core";
import {
  IconCheck,
  IconChevronDown,
  IconChevronRight,
  IconClock,
  IconDots,
  IconScissors,
  IconUser,
  IconUserCheck,
} from "@tabler/icons-react";
import { useSalon } from "../../../../../context/SalonContext";
import useReceiptPrint from "../../../../common/useReceiptPrint";
import usePermissions from "../../../../../hooks/usePermissions";
import { useAppointmentForms } from "../../../../../hooks/useAppointmentForms";
import type { AppointmentForm } from "../../../../../types/appointmentForms";
import type { AppointmentTag } from "../../../../../types/appointmentTags";
import {
  AppointmentStatus,
  CalendarEventType,
  SELECTABLE_STATUSES,
} from "../../../../../types/calendar";
import { Client } from "../../../../../types/client";
import type { Employee, SalonService } from "../../../../../types/salon";
import type {
  CalendarEvent,
  GetTransactionsResponse,
} from "../../../../../types/transactions";
import {
  badgeSettingsFrom,
  type BadgeSettings,
} from "../../../../../utils/badgeSettings";
import {
  canReschedule,
  isAwaitingConfirmation,
  statusPickBlockedReason,
} from "../../booking/guards";
import {
  arrivalArea,
  checkoutLabel,
  CLOSED_STATUSES,
  scheduledMessagesCount,
} from "../../appointment/appointmentModel";
import {
  FormsScreen,
  MessagesScreen,
  NotesScreen,
} from "../../appointment/AppointmentScreens";
import NeedToKnow from "../../appointment/NeedToKnow";
import AppointmentTagsRow from "../../appointment/AppointmentTagsRow";
import { usePreviewClientNotes } from "../../appointment/usePreviewClientNotes";
import drawerStyles from "../../appointment/AppointmentDrawer.module.css";
import SummaryRow from "../../drawer/SummaryRow";
import {
  transactionFlags,
  withoutTagsFlag,
  withPinnedNotes,
} from "../../flags/calendarFlags";
import { MobileAppt } from "../grid/geometry";
import { MobileFullScreen } from "../sheets/MobileFullScreen";
import { MobileSheet } from "../sheets/MobileSheet";
import {
  ACCEPTED_UNCONFIRMED_TONE,
  MOBILE_Z,
  STATUS_TONES,
  StatusTone,
} from "../tokens";
import { processingRowText, serviceRowText, whenRowText } from "./detailModel";
import { MobileRescheduleSection } from "./MobileRescheduleSection";
import styles from "./MobileDetailSheet.module.css";

/** What the sheet asks the calendar to do (same paths as desktop's popover). */
export interface MobileDetailActions {
  /** Status write (useTransactionStore.applyAppointmentStatus). */
  onStatusChange: (
    transaction: GetTransactionsResponse,
    status: AppointmentStatus,
  ) => Promise<unknown>;
  /** Open Appointment Details (TransactionDetailModal). */
  onView: (transaction: GetTransactionsResponse) => void;
  onRebook: (transaction: GetTransactionsResponse) => void;
  /** FormViewer for one form / Add Form/SOAP library (usePreviewPopover). */
  onOpenForm: (transaction: GetTransactionsResponse, formId: string) => void;
  onAddForm: (transaction: GetTransactionsResponse) => void;
  /** Transactions whose forms were sent this session. */
  sentFormTxnIds: ReadonlySet<string>;
  onFormsSent: (transactionId: string) => void;
  /** Forms changed in the sheet (e.g. one removed): sync the store. */
  onFormsChange?: (
    transaction: GetTransactionsResponse,
    forms: AppointmentForm[],
  ) => void;
  /** The Tags row saved: sync the store (no row when absent). */
  onTagsChange?: (
    transaction: GetTransactionsResponse,
    tags: AppointmentTag[],
  ) => void;
}

export interface MobileAppointmentDetailSheetProps {
  /** The tapped appointment block (live), or null when closed. */
  appt: MobileAppt | null;
  /** Its transaction from the store (live), or null when closed. */
  transaction: GetTransactionsResponse | null;
  timezone: string;
  /** Calendar events for reschedule conflicts (all staff). */
  events: CalendarEventType[];
  /** Bookable active staff. */
  staff: Employee[];
  actions: MobileDetailActions;
  onClose: () => void;
  /** Reschedule saved: the new day + transaction ids to refetch. */
  onRescheduled: (day: string, transactionIds: string[]) => void;
}

const toneVars = (tone: StatusTone): React.CSSProperties =>
  ({
    "--tone-bg": tone.bg,
    "--tone-fg": tone.fg,
    "--tone-bd": tone.bd,
  }) as React.CSSProperties;

const toneOf = (status: string): StatusTone =>
  STATUS_TONES[status] || STATUS_TONES.Requested;

const ICON = { size: 16, stroke: 1.8 } as const;

type Mode = "detail" | "reschedule";
type Nested = "forms" | "notes" | "messages";

const NESTED_TITLE: Record<Nested, string> = {
  forms: "Forms",
  notes: "Notes",
  messages: "Scheduled messages",
};

interface DetailSheetProps extends Omit<
  MobileAppointmentDetailSheetProps,
  "appt" | "transaction"
> {
  appt: MobileAppt;
  transaction: GetTransactionsResponse;
}

const DetailSheet: React.FC<DetailSheetProps> = ({
  appt,
  transaction,
  timezone,
  events,
  staff,
  actions,
  onClose,
  onRescheduled,
}) => {
  const { businessInfo, services } = useSalon() as {
    businessInfo: ({ id?: string } & Partial<BadgeSettings>) | null;
    services: SalonService[] | undefined;
  };
  const salonUuid = businessInfo?.id;
  const { printReceipt, receipt } = useReceiptPrint();
  const canViewClientForms =
    usePermissions()?.formsAndWaivers?.canViewClientForms === true;

  const [mode, setMode] = useState<Mode>("detail");
  const [statusOpen, setStatusOpen] = useState(false);
  const [statusSaving, setStatusSaving] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [nested, setNested] = useState<Nested | null>(null);

  const event = appt.event as CalendarEvent;
  const txnId = transaction.transaction.id;
  const status =
    transaction.transaction.appointment_status || event.status || "Confirmed";
  // The header chip shows the display-only "Accepted (Unconfirmed)" state;
  // the status panel options stay the real statuses.
  const tone =
    status === "Accepted" && isAwaitingConfirmation(transaction, timezone)
      ? ACCEPTED_UNCONFIRMED_TONE
      : toneOf(status);
  const statusLocked = status === "Completed";

  const client = transaction.client;
  const clientId = client?.id ?? event.clientUuid;
  const clientName = client
    ? `${client.first_name} ${client.last_name}`.trim()
    : appt.client;

  // ── Forms / notes (same hooks as the desktop drawer) ──────────────────
  const initialForms = useMemo(
    () => transaction.forms ?? [],
    [transaction.forms],
  );
  const { onFormsChange, onTagsChange } = actions;
  const formsApi = useAppointmentForms(txnId, {
    initialForms,
    salonUuid,
    clientUuid: client?.id,
    reseedOnInitialFormsChange: true,
    onFormsChange: onFormsChange
      ? (forms) => onFormsChange(transaction, forms)
      : undefined,
  });
  const notesApi = usePreviewClientNotes(clientId);
  const notesClient = client ?? notesApi.cachedClient;

  // ── Need to know + arrival (shared with the drawer) ───────────────────
  const badgeSettings = badgeSettingsFrom(businessInfo);
  const flags = withoutTagsFlag(
    withPinnedNotes(
      transactionFlags(transaction, timezone, badgeSettings),
      notesApi.popupNotes.map((n) => n.text),
      badgeSettings,
    ),
  );
  const area = arrivalArea(transaction, timezone);

  // ── Footer gating ─────────────────────────────────────────────────────
  // Unlike the drawer, Reschedule stays after the appointment ended (mobile
  // behavior since its reschedule section shipped).
  const hasBookedServices =
    (transaction.line_items?.booked_services?.length ?? 0) > 0;
  const showReschedule = hasBookedServices && canReschedule(transaction);
  const closed = CLOSED_STATUSES.has(status);

  const pickStatus = async (next: AppointmentStatus) => {
    setStatusOpen(false);
    if (next === status || statusSaving) return;
    setStatusSaving(true);
    try {
      await actions.onStatusChange(transaction, next);
    } finally {
      setStatusSaving(false);
    }
  };

  // Escape / back: provider picker → status panel → sheet (README order).
  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key !== "Escape") return;
    e.preventDefault();
    e.stopPropagation();
    if (pickerOpen) setPickerOpen(false);
    else if (nested) setNested(null);
    else if (statusOpen) setStatusOpen(false);
    else onClose();
  };

  const processing = processingRowText(appt);
  const providerEmployee = staff.find((e) => e.id === appt.staffId);
  const providerName =
    event.employeeName ||
    (providerEmployee
      ? `${providerEmployee.first_name ?? ""} ${providerEmployee.last_name ?? ""}`.trim()
      : "");

  const detailHeader = (
    <Box className={styles.headBlock}>
      <Box className={styles.statusWrap}>
        {statusLocked ? (
          <Box
            className={styles.statusChip}
            data-locked="true"
            style={toneVars(tone)}
            title="Completed is set at checkout and can't be changed here"
          >
            {tone.label}
          </Box>
        ) : (
          <UnstyledButton
            className={styles.statusChip}
            style={toneVars(tone)}
            onClick={() => setStatusOpen((o) => !o)}
            disabled={statusSaving}
            aria-haspopup="listbox"
            aria-expanded={statusOpen}
            aria-label={`Appointment status: ${tone.label}. Change appointment status`}
          >
            <Box component="span">{tone.label}</Box>
            <IconChevronDown size={14} stroke={1.8} />
          </UnstyledButton>
        )}
        {statusOpen && (
          <Box className={styles.statusPanel} role="listbox">
            {SELECTABLE_STATUSES.map((s) => {
              const t = toneOf(s);
              // Same gate as desktop's status menu (Arrived window). Shown
              // disabled with the reason inline: touch has no hover tooltip.
              const blockedReason =
                s === status
                  ? null
                  : statusPickBlockedReason(s, transaction, timezone);
              return (
                <UnstyledButton
                  key={s}
                  role="option"
                  aria-selected={s === status}
                  aria-disabled={blockedReason ? true : undefined}
                  data-blocked={blockedReason ? "true" : undefined}
                  className={styles.statusOption}
                  onClick={() => {
                    if (!blockedReason) void pickStatus(s);
                  }}
                >
                  <Box
                    component="span"
                    className={styles.chip}
                    style={toneVars(t)}
                  >
                    {t.label}
                  </Box>
                  {blockedReason && (
                    <Box component="span" className={styles.statusHint}>
                      {blockedReason}
                    </Box>
                  )}
                </UnstyledButton>
              );
            })}
          </Box>
        )}
      </Box>
      {clientId ? (
        <Anchor
          component={Link}
          to={`/admin/clients/${clientId}`}
          state={{ targetTab: "overview" }}
          className={styles.titleRow}
          underline="never"
        >
          <Text component="span" className={styles.clientName}>
            {clientName}
          </Text>
          <Box component="span" className={styles.titleChev}>
            <IconChevronRight size={17} stroke={1.8} />
          </Box>
        </Anchor>
      ) : (
        <Box className={styles.titleRow}>
          <Text component="span" className={styles.clientName}>
            {clientName}
          </Text>
        </Box>
      )}
    </Box>
  );

  const rescheduleHeader = (
    <Box className={styles.headBlock}>
      <Text className={styles.plainTitle}>Reschedule</Text>
      <Text className={styles.subtitle}>
        Move this appointment to a new slot
      </Text>
    </Box>
  );

  return (
    <MobileSheet
      opened
      onClose={onClose}
      title={mode === "detail" ? clientName : "Reschedule"}
      header={mode === "detail" ? detailHeader : rescheduleHeader}
      closeOnEscape={false}
      onKeyDown={handleKeyDown}
      data-testid="mobile-detail-sheet"
    >
      {mode === "detail" ? (
        <Box>
          <Box className={styles.rows}>
            <Box className={styles.row}>
              <Box component="span" className={styles.rowIcon}>
                <IconScissors {...ICON} />
              </Box>
              <Text component="span" className={styles.rowText} inherit>
                {serviceRowText(appt)}
              </Text>
            </Box>
            {providerName && (
              <Box className={styles.row}>
                <Box component="span" className={styles.rowIcon}>
                  <IconUser {...ICON} />
                </Box>
                <Text component="span" className={styles.rowText} inherit>
                  {providerName}
                </Text>
              </Box>
            )}
            <Box className={styles.row}>
              <Box component="span" className={styles.rowIcon}>
                <IconClock {...ICON} />
              </Box>
              <Text component="span" className={styles.rowText} inherit>
                {whenRowText(appt, new Date(event.start))}
              </Text>
            </Box>
            {processing && (
              <Box className={styles.row} data-muted="true">
                <Box component="span" className={styles.rowIcon}>
                  <IconClock {...ICON} />
                </Box>
                <Text component="span" className={styles.rowText} inherit>
                  {processing}
                </Text>
              </Box>
            )}
          </Box>

          {onTagsChange && (
            <AppointmentTagsRow
              transactionId={txnId}
              tags={transaction.tags ?? []}
              onChange={(tags) => onTagsChange(transaction, tags)}
            />
          )}

          {/* Request / arrival (one at most; same model as the drawer) */}
          {area?.kind === "request" && (
            <Box className={drawerStyles.requestCard}>
              <Text className={drawerStyles.cardTitle}>
                Waiting for your approval
              </Text>
              <Group gap="xs" grow mt={10}>
                <Button
                  color="teal"
                  h={44}
                  onClick={() => void pickStatus("Accepted")}
                  loading={statusSaving}
                >
                  Accept
                </Button>
                <Button
                  variant="default"
                  h={44}
                  onClick={() => void pickStatus("Declined")}
                  disabled={statusSaving}
                >
                  Decline
                </Button>
              </Group>
            </Box>
          )}
          {area?.kind === "markArrived" && (
            <Box className={drawerStyles.arriveCard}>
              <Text className={drawerStyles.cardTitle}>{area.copy}</Text>
              <Button
                color="teal"
                h={44}
                mt={10}
                fullWidth
                leftSection={<IconUserCheck size={17} />}
                onClick={() => void pickStatus("Arrived")}
                loading={statusSaving}
              >
                Mark arrived
              </Button>
            </Box>
          )}
          {area?.kind === "arrived" && (
            <Group
              justify="space-between"
              wrap="nowrap"
              className={drawerStyles.arrivedStrip}
              data-testid="arrived-strip"
            >
              <Group gap={6} wrap="nowrap">
                <IconCheck size={16} />
                <Text className={drawerStyles.arrivedText}>{area.label}</Text>
              </Group>
              <Button
                variant="subtle"
                h={44}
                className={drawerStyles.undo}
                onClick={() => void pickStatus("Confirmed")}
                disabled={statusSaving}
              >
                Undo
              </Button>
            </Group>
          )}

          <NeedToKnow flags={flags} />

          {/* Summary rows → full-screen nested screens */}
          <Stack gap={8} mt={16}>
            {canViewClientForms && (
              <SummaryRow
                label="Forms"
                count={formsApi.totalCount}
                onOpen={() => setNested("forms")}
              />
            )}
            <SummaryRow
              label="Scheduled messages"
              count={scheduledMessagesCount(transaction)}
              onOpen={() => setNested("messages")}
            />
            <SummaryRow
              label="Notes"
              count={notesApi.notes.length}
              onOpen={() => setNested("notes")}
            />
          </Stack>

          {/* Footer (same order as the drawer) */}
          <Box className={styles.actions}>
            <Button
              color="teal"
              h={44}
              fullWidth
              onClick={() => actions.onView(transaction)}
            >
              {checkoutLabel(transaction)}
            </Button>
            {showReschedule && (
              <Button
                variant="default"
                color="teal"
                h={44}
                fullWidth
                className={styles.rescheduleBtn}
                onClick={() => setMode("reschedule")}
              >
                Edit / Reschedule
              </Button>
            )}
            <Group gap={9} wrap="nowrap">
              {hasBookedServices && (
                <Button
                  variant="default"
                  h={44}
                  style={{ flex: 1 }}
                  onClick={() => actions.onRebook(transaction)}
                >
                  Prebook
                </Button>
              )}
              <Menu
                position="top-end"
                width={250}
                radius={12}
                shadow="md"
                zIndex={MOBILE_Z.sheetScreen}
              >
                <Menu.Target>
                  <ActionIcon
                    variant="default"
                    size={44}
                    radius={8}
                    aria-label="More actions"
                    ml={hasBookedServices ? undefined : "auto"}
                  >
                    <IconDots size={18} />
                  </ActionIcon>
                </Menu.Target>
                <Menu.Dropdown>
                  <Menu.Item onClick={() => printReceipt(transaction)}>
                    Print receipt
                  </Menu.Item>
                  <Menu.Divider />
                  {!closed && (
                    <Menu.Item onClick={() => void pickStatus("No Show")}>
                      Mark as no-show
                    </Menu.Item>
                  )}
                  {!closed && <Menu.Divider />}
                  <Menu.Item
                    className={drawerStyles.destructive}
                    onClick={() => void pickStatus("Cancelled")}
                    disabled={status === "Cancelled"}
                  >
                    Cancel appointment
                  </Menu.Item>
                </Menu.Dropdown>
              </Menu>
            </Group>
          </Box>

          <MobileFullScreen
            opened={nested !== null}
            onClose={() => setNested(null)}
            title={NESTED_TITLE[nested ?? "notes"]}
          >
            {nested === "forms" && (
              <FormsScreen
                transaction={transaction}
                formsApi={formsApi}
                status={status}
                onBack={() => setNested(null)}
                onOpenForm={actions.onOpenForm}
                onAddForm={actions.onAddForm}
                formsAlreadySent={actions.sentFormTxnIds.has(txnId)}
                onFormsSent={actions.onFormsSent}
              />
            )}
            {nested === "notes" && (
              <NotesScreen
                notesApi={notesApi}
                client={notesClient ?? null}
                onBack={() => setNested(null)}
              />
            )}
            {nested === "messages" && (
              <MessagesScreen
                transactionUuid={txnId}
                timezone={timezone}
                hasPendingForms={formsApi.pendingCount > 0}
                status={status}
                onBack={() => setNested(null)}
              />
            )}
          </MobileFullScreen>

          {/* Single hidden print copy (portaled) for the More → Print receipt. */}
          {receipt}
        </Box>
      ) : (
        <MobileRescheduleSection
          transaction={transaction}
          bookedServiceId={event.id}
          timezone={timezone}
          services={services || []}
          staff={staff}
          events={events}
          pickerOpen={pickerOpen}
          onPickerOpenChange={setPickerOpen}
          onSaved={onRescheduled}
        />
      )}
    </MobileSheet>
  );
};

/**
 * Appointment detail sheet (README screen 4; mirrors the desktop appointment
 * drawer): status chip dropdown, client link, service / provider / when /
 * processing rows, the request / arrival area, Need to know, Forms /
 * Scheduled messages / Notes rows opening full-screen nested screens, and
 * Checkout / Edit-Reschedule / Prebook / More. Reschedule (screen 6) swaps
 * the body for the reschedule section. Mounted per open, so its state resets
 * each time.
 */
export const MobileAppointmentDetailSheet: React.FC<
  MobileAppointmentDetailSheetProps
> = ({ appt, transaction, ...rest }) =>
  appt && transaction ? (
    <DetailSheet appt={appt} transaction={transaction} {...rest} />
  ) : null;
